/* Build Your Bundle — cart integrity.
   Removes bundle offer / gift lines whose bundle is no longer in the cart, and removes every line of a bundle
   if any of its flavor lines is removed/changed before the cart transform merges them.
   Bare bundle guard: the Build Your Bundle parent product (variants from shop metafield bundle.tiers,
   injected as window.__bybTiers) can only enter the cart through the builder. Any other add-to-cart of
   it (Klaviyo Customer Hub sticky button, quick-add widgets, native product forms) is refused, and a
   bare parent line that still gets in is removed. The merged bundle line always carries _bundle_id. */
(() => {
  if (window.__bybCart) return;
  window.__bybCart = true;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let running = false;

  const prop = (item, key) => (item.properties && item.properties[key]) || null;

  const BARE_MESSAGE = 'Build your box on the Build Your Brownie Box page to pick your flavors.';
  const parentIds = (() => {
    try {
      let t = window.__bybTiers;
      if (typeof t === 'string') t = JSON.parse(t);
      const tiers = (t && t.tiers) || {};
      return new Set(
        Object.values(tiers)
          .map((x) => Number(String((x && x.variant) || '').split('/').pop()))
          .filter(Boolean),
      );
    } catch (e) {
      return new Set();
    }
  })();
  const isParent = (id) => parentIds.has(Number(id));

  // --- Bare bundle guard -------------------------------------------------------------------------
  const isAddUrl = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));

  // Items being added, from any body shape /cart/add accepts: JSON, FormData, URLSearchParams, form string.
  function itemsFromBody(body) {
    if (!body) return [];
    if (typeof body === 'string') {
      try {
        const j = JSON.parse(body);
        return Array.isArray(j.items) ? j.items : [j];
      } catch (e) {
        return itemsFromParams(new URLSearchParams(body));
      }
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) return itemsFromParams(body);
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return itemsFromParams(body);
    return [];
  }
  function itemsFromParams(params) {
    const id = params.get('id');
    if (!id) return [];
    return [{ id, properties: { _bundle_id: params.get('properties[_bundle_id]') } }];
  }
  const addsBareParent = (body) =>
    parentIds.size > 0 &&
    itemsFromBody(body).some((i) => i && isParent(i.id) && !(i.properties && i.properties._bundle_id));

  function sendToBuilder() {
    const anchor = document.getElementById('buy-box') || document.querySelector('bundle-builder');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  const refusal = () =>
    new Response(JSON.stringify({ status: 422, message: 'Cart Error', description: BARE_MESSAGE }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });

  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    if (isAddUrl(url) && init && addsBareParent(init.body)) {
      console.warn('[bundle-cart] Blocked adding the bundle product without flavors.');
      sendToBuilder();
      return Promise.resolve(refusal());
    }
    return origFetch.apply(this, arguments);
  };
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__bybUrl = url;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (isAddUrl(this.__bybUrl) && addsBareParent(body)) {
      console.warn('[bundle-cart] Blocked adding the bundle product without flavors.');
      sendToBuilder();
      this.abort();
      return;
    }
    return origSend.apply(this, arguments);
  };
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || !isAddUrl(form.action) || !addsBareParent(new FormData(form))) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      sendToBuilder();
    },
    true,
  );

  async function reconcile() {
    if (running) return;
    running = true;
    try {
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const bundles = {};
      cart.items.forEach((item) => {
        const id = prop(item, '_bundle_id');
        if (!id) return;
        const b = (bundles[id] = bundles[id] || { merged: false, flavorQty: 0, size: 0, keys: [], offers: [] });
        const role = prop(item, '_bundle_role');
        if (role === 'offer' || role === 'gift') {
          b.offers.push(item.key);
        } else if (role === 'flavor') {
          b.flavorQty += item.quantity;
          b.size = parseInt(prop(item, '_bundle_size'), 10) || 0;
          b.keys.push(item.key);
        } else {
          // Merged parent line produced by the cart transform.
          b.merged = true;
        }
      });

      const updates = {};
      // Bare parent line (got in some way the guard didn't see): no flavors, remove it.
      cart.items.forEach((item) => {
        if (isParent(item.variant_id) && !prop(item, '_bundle_id')) updates[item.key] = 0;
      });
      Object.values(bundles).forEach((b) => {
        const unmergedComplete = b.size > 0 && b.flavorQty === b.size;
        const intact = b.merged || unmergedComplete;
        if (!intact) {
          b.offers.forEach((k) => (updates[k] = 0));
          b.keys.forEach((k) => (updates[k] = 0));
        }
      });
      if (!Object.keys(updates).length) return;

      const drawer = document.querySelector('cart-drawer');
      const body = { updates };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      const res = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json());

      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
        window.location.reload();
        return;
      }
      if (drawer && res.sections) {
        if (res.item_count === 0) drawer.classList.add('is-empty');
        // Same as Dawn's renderContents, minus auto-opening the drawer.
        drawer.getSectionsToRender().forEach((section) => {
          const html = res.sections[section.id];
          const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          if (!html || !target) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector(section.selector || '.shopify-section') || doc.body;
          target.innerHTML = source.innerHTML;
        });
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-cart', cartData: res });
      }
      // Zipify cart drawer: re-read the cart so removed lines disappear.
      try {
        const zi = window.Zipify && window.Zipify.Cart && window.Zipify.Cart.instance;
        if (zi && typeof zi.updateCart === 'function') zi.updateCart();
      } catch (e) { /* noop */ }
    } catch (e) {
      console.warn('[bundle-cart]', e);
    } finally {
      running = false;
    }
  }

  const init = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => {
        if (evt && evt.source === 'bundle-cart') return;
        setTimeout(reconcile, 50);
      });
    }
    reconcile();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
